import React from 'react';
import { Building, Briefcase, Globe, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BrandOverviewTab } from './BrandOverviewTab';
import { BrandCampaignsTab } from './BrandCampaignsTab';
import { formatCurrency } from '@/lib/utils/currency';
import type { BrandWithMetrics } from '@/hooks/useAllBrandMetrics';
import type { NavigateFunction } from 'react-router-dom';

export interface BrandDetailViewProps {
  selectedBrand: BrandWithMetrics;
  activeTab: string;
  onTabChange: (tabId: string) => void;
  onEditBrand: () => void;
  brandMetrics: unknown;
  loadingMetrics: boolean;
  brandCampaigns: unknown[] | undefined;
  loadingCampaigns: boolean;
  userCountry?: string | null;
  onViewCampaigns: () => void;
  onCampaignClick: (campaignId: string) => void;
  navigate: NavigateFunction;
}

const tabs = [
  { id: 'overview', label: 'Overview', icon: Building },
  { id: 'campaigns', label: 'Campaigns', icon: Briefcase },
];

export const BrandDetailView: React.FC<BrandDetailViewProps> = ({
  selectedBrand,
  activeTab,
  onTabChange,
  onEditBrand,
  brandMetrics,
  loadingMetrics,
  brandCampaigns,
  loadingCampaigns,
  userCountry,
  onViewCampaigns,
  onCampaignClick,
  navigate,
}) => {
  const formatROI = (roi: number) => `${roi}%`;

  return (
    <div className="lg:col-span-2 flex flex-col min-h-0 h-full">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full min-h-0">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white rounded-lg border border-gray-200 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                {selectedBrand.logoUrl ? (
                  <img
                    src={selectedBrand.logoUrl}
                    alt={selectedBrand.name}
                    className="object-contain w-full h-full p-1.5"
                  />
                ) : (
                  <span className="text-purple-600 font-bold text-2xl">
                    {selectedBrand.name?.[0]}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedBrand.name}</h2>
                <p className="text-gray-600">{selectedBrand.industry || 'No industry specified'}</p>
                <div className="flex items-center space-x-4 mt-2">
                  {selectedBrand.websiteUrl && (
                    <a
                      href={selectedBrand.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-700"
                    >
                      <Globe className="w-4 h-4" />
                      <span className="text-sm">Website</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onEditBrand}
                className="flex items-center space-x-1"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 shrink-0">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6 flex-1 overflow-y-auto min-h-0 no-scrollbar">
          {activeTab === 'overview' && (
            <BrandOverviewTab
              selectedBrand={selectedBrand}
              brandMetrics={brandMetrics}
              loadingMetrics={loadingMetrics}
              formatCurrency={(amount) => formatCurrency(amount, userCountry)}
              formatROI={formatROI}
            />
          )}

          {activeTab === 'campaigns' && (
            <BrandCampaignsTab
              brandCampaigns={brandCampaigns || []}
              loadingCampaigns={loadingCampaigns}
              handleViewCampaigns={onViewCampaigns}
              handleCampaignClick={onCampaignClick}
              formatCurrency={(amount) => formatCurrency(amount, userCountry)}
              navigate={navigate}
            />
          )}
        </div>
      </div>
    </div>
  );
};
